import type { TBaseResponse } from "@/models/common.model"
import type { TApiCompetitionRequest, TCompetitionData, TCompetitionDataRaw } from "@/services/settings/competition"
import type { TCompetitionStyles } from "@/services/settings/competition-styles"
import { format, parse } from "date-fns"
import { languageParser } from "../language.mapper"

export type TCompetitionResponse = TBaseResponse<TCompetitionDataRaw[]>

export class CompetitionMapper {
  // Overloads: devuelve array cuando entra array, objeto cuando entra objeto.
  static toDto(rawData: TCompetitionDataRaw[]): TCompetitionData[]
  static toDto(rawData: TCompetitionDataRaw): TCompetitionData
  static toDto(rawData: TCompetitionDataRaw | TCompetitionDataRaw[] | null | undefined): TCompetitionData | TCompetitionData[] {
    if (!rawData)
      return []

    const mapOne = (competition: TCompetitionDataRaw): TCompetitionData => ({
      id: competition.id ?? undefined,
      name: competition.name ?? "",
      code: competition.code ?? "",
      description: languageParser(competition.description) ?? "",
      metadata: {
        discipline: competition.metadata?.discipline ?? "",
        sportEvent: competition.metadata?.sportEvent ?? "",
        modality: competition.metadata?.modality ?? "",
        event: competition.metadata?.event ?? "",
        gender: competition.metadata?.gender ?? "",
        category: competition.metadata?.category ?? "",
        rscCode: competition.metadata?.rscCode ?? "",
      },
      order: competition.order ?? 0,
      gender: competition.metadata?.gender ?? "",
      phaseCount: competition.phaseCount ?? 0,
      dateInfo: {
        startDate: competition.dateInfo?.startDate ? parse(competition.dateInfo.startDate, "yyyy-MM-dd'T'HH:mm:ss", new Date()) : undefined,
        endDate: competition.dateInfo?.endDate ? parse(competition.dateInfo.endDate, "yyyy-MM-dd'T'HH:mm:ss", new Date()) : undefined,
      },
      venues: competition.venues ?? [],
      style: competition.style?.code ?? "individual",
    })

    return Array.isArray(rawData) ? rawData.map(mapOne) : mapOne(rawData)
  }

  static toRequestDto(competition: TCompetitionData): TApiCompetitionRequest {
    return {
      name: competition.name ?? "",
      code: competition.code ?? "",
      description: languageParser(competition.description) ?? "",
      metadata: {
        discipline: competition.metadata?.discipline ?? "",
        sportEvent: competition.metadata?.sportEvent ?? "",
        modality: competition.metadata?.modality ?? "",
        event: competition.metadata?.event ?? "",
        gender: competition.metadata?.gender ?? "",
        category: competition.metadata?.category ?? "",
        rscCode: competition.metadata?.rscCode ?? "",
      },
      order: competition.order ?? 0,
      gender: competition.metadata?.gender ?? "",
      style: competition.style ?? "individual",
      phaseCount: competition.phaseCount ?? 0,
      dateInfo: {
        startDate: competition.dateInfo?.startDate ? format(competition.dateInfo.startDate, "yyyy-MM-dd'T'HH:mm:ss") : undefined,
        endDate: competition.dateInfo?.endDate ? format(competition.dateInfo.endDate, "yyyy-MM-dd'T'HH:mm:ss") : undefined,
      },
      venues: competition.venues ?? [],
    }
  }
}

export class CompetitionStylesMapper {
  static toDto(rawData: TCompetitionStyles[] | null): TCompetitionStyles[] {
    if (!rawData) {
      return []
    }

    return rawData.map(competition => ({
      code: competition.code ?? "",
      description: languageParser(competition.description) ?? "",
      order: competition.order ?? 0,
    }))
  }
}
